import { Globe, IdCard, Linkedin, Navigation } from "lucide-react";
import {
  useGetIdentity,
  useNotify,
  useRefresh,
  useUpdate,
  useLocaleState,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin/delete-button";
import { ShowButton } from "@/components/admin/show-button";
import { TextField } from "@/components/admin/text-field";
import { UrlField } from "@/components/admin/url-field";
import { SelectField } from "@/components/admin/select-field";

import { formatLocalizedDate } from "../misc/RelativeDate";
import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";
import { useGetSalesName } from "../sales/useGetSalesName";
import { CompanyEnrich } from "./CompanyEnrich";
import { CompanyLead } from "./CompanyLead";
import { CompanyEmbassy } from "./CompanyEmbassy";
import { ContactBlockBanner, ContactBlockButton } from "./ContactBlock";
import { LocationInfo } from "../locations/LocationInfo";
import { ParticipationsAside } from "../deals/DealParticipants";
import { PhoneLink, SocialLink } from "../misc/ContactLinks";
import { cleanContextLinks, socialPlatform } from "../misc/socialLinks";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <div className="hidden sm:block w-64 min-w-64 lg:w-92 lg:min-w-92 space-y-4">
      <div className="flex flex-row flex-wrap gap-1">
        {link === "edit" ? (
          <EditButton label={translate("resources.companies.action.edit")} />
        ) : (
          <ShowButton label={translate("resources.companies.action.show")} />
        )}
        <HideCompanyButton record={record} />
        <ContactBlockButton record={record} />
      </div>
      <ContactBlockBanner company={record} />
      {record.hidden && (
        <p className="text-xs rounded-md bg-muted px-3 py-2 text-muted-foreground">
          Diese Firma ist ausgeblendet: nicht in der Firmenliste, ihre E-Mails landen nicht in „Zu prüfen“.
        </p>
      )}

      <CompanyLead record={record} />

      <ParticipationsAside companyId={record.id} />

      <LocationInfo record={record} />

      <CompanyEmbassy record={record} />

      <CompanyEnrich record={record} />

      <CompanyInfo record={record} />

      <AddressInfo record={record} />

      <ContextInfo record={record} />

      <AdditionalInfo record={record} />

      {link !== "edit" && (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <DeleteButton
            className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

export const CompanyInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const socials = cleanContextLinks(record.context_links ?? [], record.linkedin_url).filter((l) => socialPlatform(l));
  if (
    !record.website &&
    !record.linkedin_url &&
    !record.phone_number &&
    !record.phone_numbers?.length &&
    !record.customer_number &&
    !socials.length
  ) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.contact")}
    >
      {record.website && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Globe className="w-4 h-4" />
          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
      {record.linkedin_url && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Linkedin className="w-4 h-4" />
          <a
            className="underline hover:no-underline"
            href={record.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            title={record.linkedin_url}
          >
            LinkedIn
          </a>
        </div>
      )}
      {record.phone_number && <PhoneLink number={record.phone_number} disabled={record.contact_blocked} name={record.name} companyId={record.id} />}
      {(record.phone_numbers ?? []).map((p) => (
        <PhoneLink key={p.number} number={p.number} label={p.label} disabled={record.contact_blocked} name={record.name} companyId={record.id} />
      ))}
      {socials
        .map((l) => (
          <SocialLink key={l} url={l} />
        ))}
      {record.customer_number && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <IdCard className="w-4 h-4" />
          <span className="text-sm">
            {translate("resources.companies.fields.customer_number")}:{" "}
            <strong>{record.customer_number}</strong>
          </span>
        </div>
      )}
    </AsideSection>
  );
};

export const ContextInfo = ({ record }: { record: Company }) => {
  const { companySectors } = useConfigurationContext();
  const translate = useTranslate();
  if (!record.revenue && !record.id) {
    return null;
  }

  const sector = companySectors.find((s) => s.value === record.sector);
  const sectorLabel = sector?.label;
  const translatedSizes = sizes.map((size) => ({
    ...size,
    name: getTranslatedCompanySizeLabel(size, translate),
  }));

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.context")}
    >
      {sectorLabel && (
        <span>
          {translate("resources.companies.fields.sector")}: {sectorLabel}
        </span>
      )}
      {record.size && (
        <span>
          {translate("resources.companies.fields.size")}:{" "}
          <SelectField source="size" choices={translatedSizes} />
        </span>
      )}
      {record.revenue && (
        <span>
          {translate("resources.companies.fields.revenue")}:{" "}
          <TextField source="revenue" />
        </span>
      )}
      {record.tax_identifier && (
        <span>
          {translate("resources.companies.fields.tax_identifier", {})}
          : <TextField source="tax_identifier" />
        </span>
      )}
    </AsideSection>
  );
};

// LDF: Route per Google Maps – Start ist der aktuelle Standort des Geräts
export const routeHref = (record: Pick<Company, "address" | "zipcode" | "city" | "country">) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    [record.address, [record.zipcode, record.city].filter(Boolean).join(" "), record.country].filter(Boolean).join(", "),
  )}&travelmode=driving`;

export const AddressInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  if (
    !record.address &&
    !record.city &&
    !record.zipcode &&
    !record.state_abbr
  ) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.address")}
      noGap
      action={
        <a
          href={routeHref(record)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-6 items-center gap-1 rounded-md border border-border px-2 text-xs no-underline hover:bg-muted"
          title="Route von deinem aktuellen Standort in Google Maps öffnen"
        >
          <Navigation className="h-3 w-3" /> Route
        </a>
      }
    >
      <TextField source="address" />
      {(record.zipcode || record.city) && <span>{[record.zipcode, record.city].filter(Boolean).join(" ")}</span>}
      <TextField source="state_abbr" />
      <TextField source="country" />
    </AsideSection>
  );
};

export const AdditionalInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const [locale = "de"] = useLocaleState();
  const { identity } = useGetIdentity();
  const isCurrentUser = record.sales_id === identity?.id;
  const salesName = useGetSalesName(record.sales_id, {
    enabled: !isCurrentUser,
  });
  if (
    !record.created_at &&
    !record.sales_id &&
    !record.description &&
    !record.context_links
  ) {
    return null;
  }
  const getBaseURL = (url: string) => {
    const urlObject = new URL(url.startsWith("http") ? url : `https://${url}`);
    return urlObject.hostname;
  };

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.additional_info")}
    >
      {record.description && (
        <p className="text-sm  mb-1">{record.description}</p>
      )}
      {record.context_links && (
        <div className="flex flex-col">
          {record.context_links.map((link, index) =>
            link && !socialPlatform(link) ? (
              <a
                key={index}
                className="text-sm underline hover:no-underline mb-1"
                href={link.startsWith("http") ? link : `https://${link}`}
                target="_blank"
                rel="noopener noreferrer"
                title={link}
              >
                {getBaseURL(link)}
              </a>
            ) : null,
          )}
        </div>
      )}
      {record.sales_id !== null && (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          {translate(
            isCurrentUser
              ? "resources.companies.followed_by_you"
              : "resources.companies.followed_by",
            { name: salesName },
          )}
        </div>
      )}
      {record.created_at && (
        <p className="text-sm text-muted-foreground mb-1">
          {translate("resources.companies.added_on", {
            date: formatLocalizedDate(record.created_at, locale),
          })}{" "}
        </p>
      )}
    </AsideSection>
  );
};

const HideCompanyButton = ({ record }: { record: Company }) => {
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const hide = !record.hidden;
  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() =>
        update(
          "companies",
          { id: record.id, data: { hidden: hide }, previousData: record },
          {
            onSuccess: () => {
              notify(hide ? "Firma ausgeblendet" : "Firma wieder eingeblendet", { type: "success" });
              refresh();
            },
          },
        )
      }
      className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-3 text-sm hover:bg-muted cursor-pointer"
    >
      {hide ? "Firma ausblenden" : "Wieder einblenden"}
    </button>
  );
};
